import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type {
  InstagramConversation,
  InstagramMessage,
  InstagramAgentConfig,
  InboxStats,
  ConversationStatus,
} from '@/types/outreach';

// --- Conversations ---

interface ConversationsResponse {
  count: number;
  results: InstagramConversation[];
}

export const useInstagramConversations = (search?: string, status?: ConversationStatus | '') => {
  return useInfiniteQuery({
    queryKey: ['instagram-conversations', search, status],
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, string> = { page: String(pageParam), page_size: '20' };
      if (search) params.search = search;
      if (status) params.status = status;
      const { data } = await api.get<ConversationsResponse>(API_ROUTES.instagramConversations, {
        params,
      });
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.results.length, 0);
      if (loaded >= lastPage.count) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
    refetchInterval: 5000,
  });
};

export const useInstagramConversationDetail = (id: string | null) => {
  return useQuery({
    queryKey: ['instagram-conversation', id],
    queryFn: async () => {
      const { data } = await api.get<InstagramConversation>(
        `${API_ROUTES.instagramConversations}${id}/`,
      );
      return data;
    },
    enabled: !!id,
  });
};

interface MessagesResponse {
  count: number;
  results: InstagramMessage[];
}

export const useInstagramConversationMessages = (id: string | null, after?: string) => {
  return useQuery({
    queryKey: ['instagram-messages', id, after],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (after) params.after = after;
      const { data } = await api.get<MessagesResponse>(
        `${API_ROUTES.instagramConversations}${id}/messages/`,
        { params },
      );
      return data;
    },
    enabled: !!id,
    refetchInterval: 2500,
  });
};

// --- Mutations ---

export const useInstagramSendAdminMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
    }: {
      conversationId: string;
      content: string;
    }) => {
      const { data } = await api.post<InstagramMessage>(
        `${API_ROUTES.instagramConversations}${conversationId}/send/`,
        { content },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['instagram-messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['instagram-conversations'] });
    },
  });
};

export const useInstagramUpdateConversationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      status,
    }: {
      conversationId: string;
      status: ConversationStatus;
    }) => {
      const { data } = await api.patch(
        `${API_ROUTES.instagramConversations}${conversationId}/status/`,
        { status },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['instagram-conversation', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['instagram-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['instagram-inbox-stats'] });
    },
  });
};

export const useInstagramMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await api.post(
        `${API_ROUTES.instagramConversations}${conversationId}/read/`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instagram-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['instagram-inbox-stats'] });
    },
  });
};

// --- Inbox Stats ---

export const useInstagramInboxStats = () => {
  return useQuery({
    queryKey: ['instagram-inbox-stats'],
    queryFn: async () => {
      const { data } = await api.get<InboxStats>(API_ROUTES.instagramInboxStats);
      return data;
    },
    refetchInterval: 10000,
  });
};

// --- Agent Config ---

export const useInstagramAgentConfigs = () => {
  return useQuery({
    queryKey: ['instagram-agent-configs'],
    queryFn: async () => {
      const { data } = await api.get<InstagramAgentConfig[]>(API_ROUTES.instagramAgentConfig);
      return data;
    },
  });
};

export const useInstagramSaveAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      config: Partial<InstagramAgentConfig> & { name: string; systemPrompt: string },
    ) => {
      if (config.id) {
        const { data } = await api.put<InstagramAgentConfig>(
          `${API_ROUTES.instagramAgentConfig}${config.id}/`,
          config,
        );
        return data;
      }
      const { data } = await api.post<InstagramAgentConfig>(
        API_ROUTES.instagramAgentConfig,
        config,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instagram-agent-configs'] });
    },
  });
};

export const useInstagramDeleteAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.instagramAgentConfig}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instagram-agent-configs'] });
    },
  });
};
